import React, { useEffect, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  Modal,
  Popover,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import {
  IconCheck,
  IconInfoCircle,
  IconSend,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import {
  latestRejectionHistoryEntry,
  type BaselineRevisionSummary,
} from '../../../api/baselineApis';
import { useModalContainer } from '../../../contexts/ModalContainerContext';
import { busyActionButtonProps } from './taskModalButtonStyles';
import { ganttToolbarButtonProps } from './ganttToolbarStyles';

export interface ActiveBaselineRevisionBannerProps {
  revision: BaselineRevisionSummary;
  busy: boolean;
  /** Coarse gate: same permission as Set baseline for now. */
  canAct: boolean;
  /** When true, draft Submit is blocked until schedule is saved to the server. */
  hasUnsavedChanges?: boolean;
  onSubmit: () => void | Promise<void>;
  onDiscard: () => void | Promise<void>;
  onApprove: (opts: {
    setAsEarnedValueBaseline: boolean;
    reviewComment?: string;
  }) => void | Promise<void>;
  onReject: (opts: { reviewComment: string }) => void | Promise<void>;
}

type StatusTone = {
  accent: string;
  badge: string;
  surface: string;
  label: string;
};

type DialogKind = 'approve' | 'reject' | null;

/** REJECTED is presented as Draft in the Gantt banner (snapshot refresh is implicit). */
function displayStatus(
  status: BaselineRevisionSummary['status'],
): BaselineRevisionSummary['status'] {
  return status === 'REJECTED' ? 'DRAFT' : status;
}

function statusTone(status: BaselineRevisionSummary['status']): StatusTone {
  switch (displayStatus(status)) {
    case 'DRAFT':
      return {
        accent: 'var(--mantine-color-blue-6)',
        badge: 'blue',
        surface: 'color-mix(in srgb, var(--mantine-color-blue-6) 8%, var(--mantine-color-body))',
        label: 'Draft',
      };
    case 'SUBMITTED':
      return {
        accent: 'var(--mantine-color-yellow-6)',
        badge: 'yellow',
        surface:
          'color-mix(in srgb, var(--mantine-color-yellow-6) 10%, var(--mantine-color-body))',
        label: 'Awaiting approval',
      };
    default:
      return {
        accent: 'var(--mantine-color-gray-5)',
        badge: 'gray',
        surface: 'var(--mantine-color-body)',
        label: status,
      };
  }
}

function formatCreatedAt(raw?: string | null): string | null {
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Compact property row for the revision details popover. */
function FactRow({ label, value }: { label: string; value: string }) {
  return (
    <Group justify="space-between" align="flex-start" gap="lg" wrap="nowrap" py={4}>
      <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
        {label}
      </Text>
      <Text
        size="sm"
        ta="right"
        fw={500}
        style={{ wordBreak: 'break-word', lineHeight: 1.35, fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </Text>
    </Group>
  );
}

export function ActiveBaselineRevisionBanner({
  revision,
  busy,
  canAct,
  hasUnsavedChanges = false,
  onSubmit,
  onDiscard,
  onApprove,
  onReject,
}: ActiveBaselineRevisionBannerProps) {
  const modalContainer = useModalContainer();
  const [dialog, setDialog] = useState<DialogKind>(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const [setAsEv, setSetAsEv] = useState(false);
  const [reviewComment, setReviewComment] = useState('');
  const [actionPending, setActionPending] = useState(false);

  const uiStatus = displayStatus(revision.status);
  const tone = statusTone(revision.status);
  const name = revision.baselineName?.trim() || `Baseline ${revision.baselineIndex}`;
  const createdAt = formatCreatedAt(revision.createdAt);
  const reviewedAt = formatCreatedAt(revision.reviewedAt);
  const dialogBusy = busy || actionPending;
  const latestRejection = latestRejectionHistoryEntry(revision.meta);
  const previousRejectionReason = latestRejection?.reviewComment?.trim() || null;
  const reviewNote =
    previousRejectionReason || revision.reviewComment?.trim() || null;

  const factRows: Array<{ label: string; value: string }> = [
    { label: 'Status', value: tone.label },
    { label: 'Slot', value: String(revision.baselineIndex) },
    { label: 'Revision', value: String(revision.revisionNumber) },
  ];
  if (revision.taskCount != null) {
    factRows.push({ label: 'Tasks', value: String(revision.taskCount) });
  }
  if (revision.createdBy) {
    factRows.push({ label: 'Created by', value: revision.createdBy });
  }
  if (createdAt) {
    factRows.push({ label: 'Created', value: createdAt });
  }
  if (latestRejection?.reviewedBy) {
    factRows.push({ label: 'Rejected by', value: latestRejection.reviewedBy });
  } else if (revision.reviewedBy) {
    factRows.push({ label: 'Reviewed by', value: revision.reviewedBy });
  }
  const latestRejectedAt = formatCreatedAt(latestRejection?.reviewedAt);
  if (latestRejectedAt) {
    factRows.push({ label: 'Rejected', value: latestRejectedAt });
  } else if (reviewedAt) {
    factRows.push({ label: 'Reviewed', value: reviewedAt });
  }
  if (latestRejection?.revisionNumber != null) {
    factRows.push({
      label: 'Prior revision',
      value: String(latestRejection.revisionNumber),
    });
  } else if (revision.meta?.previousRevisionNumber != null) {
    factRows.push({
      label: 'Prior revision',
      value: String(revision.meta.previousRevisionNumber),
    });
  }

  useEffect(() => {
    if (dialog == null) {
      setSetAsEv(false);
      setReviewComment('');
      setActionPending(false);
    }
  }, [dialog]);

  const closeDialog = () => {
    if (dialogBusy) return;
    setDialog(null);
  };

  const runDialogAction = (action: () => void | Promise<void>) => {
    if (dialogBusy) return;
    setActionPending(true);
    void Promise.resolve(action())
      .then(() => {
        setDialog(null);
      })
      .catch(() => {
        // Parent already surfaces errors.
      })
      .finally(() => {
        setActionPending(false);
      });
  };

  const rejectComment = reviewComment.trim();
  const canConfirmReject = rejectComment.length > 0;

  return (
    <>
      <Box
        style={{
          borderRadius: 8,
          border: '1px solid var(--mantine-color-default-border)',
          borderLeft: `3px solid ${tone.accent}`,
          background: tone.surface,
        }}
      >
        <Group
          justify="space-between"
          align="center"
          gap="sm"
          wrap="nowrap"
          px="sm"
          py={8}
          style={{ minHeight: 40 }}
        >
          <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
            <Text
              size="xs"
              fw={700}
              c="dimmed"
              style={{
                flexShrink: 0,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                lineHeight: 1.2,
              }}
            >
              Baseline
            </Text>
            <Badge
              size="sm"
              variant="light"
              color={tone.badge}
              radius="sm"
              style={{ flexShrink: 0, textTransform: 'none', fontWeight: 600 }}
            >
              {tone.label}
            </Badge>
            <Group gap={4} wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
              <Group gap={4} wrap="nowrap" style={{ minWidth: 0 }}>
                <Text size="sm" fw={600} truncate style={{ lineHeight: 1.2 }}>
                  {name}
                </Text>
                <Text
                  size="xs"
                  c="dimmed"
                  style={{ flexShrink: 0, lineHeight: 1.2, whiteSpace: 'nowrap' }}
                >
                  · Slot {revision.baselineIndex}
                </Text>
              </Group>
              <Popover
                opened={infoOpen}
                onChange={setInfoOpen}
                position="bottom-start"
                width={300}
                shadow="md"
                radius="md"
                withArrow
                arrowSize={10}
                offset={8}
                withinPortal
                zIndex={400}
              >
                <Popover.Target>
                  <ActionIcon
                    variant={infoOpen ? 'light' : 'subtle'}
                    color="gray"
                    size={22}
                    radius="xl"
                    aria-label="Revision details"
                    aria-expanded={infoOpen}
                    title="Revision details"
                    onClick={() => setInfoOpen((open) => !open)}
                  >
                    <IconInfoCircle size={15} />
                  </ActionIcon>
                </Popover.Target>
                <Popover.Dropdown p="md">
                  <Stack gap="sm">
                    <Group justify="space-between" align="flex-start" gap="sm" wrap="nowrap">
                      <Group gap={6} align="baseline" wrap="nowrap" style={{ minWidth: 0 }}>
                        <Text
                          fw={600}
                          style={{
                            fontSize: 14,
                            lineHeight: 1.3,
                            wordBreak: 'break-word',
                            minWidth: 0,
                          }}
                        >
                          {name}
                        </Text>
                        <Text size="xs" c="dimmed" style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                          · Slot {revision.baselineIndex}
                        </Text>
                      </Group>
                      <Badge
                        size="sm"
                        variant="light"
                        color={tone.badge}
                        radius="sm"
                        style={{ flexShrink: 0, textTransform: 'none', fontWeight: 600 }}
                      >
                        {tone.label}
                      </Badge>
                    </Group>

                    <Box>
                      {factRows.map((row) => (
                        <FactRow key={row.label} label={row.label} value={row.value} />
                      ))}
                    </Box>

                    {reviewNote ? (
                      <Box>
                        <Text size="xs" c="dimmed" mb={4}>
                          {latestRejection ? 'Rejection reason' : 'Review note'}
                        </Text>
                        <Text size="sm" style={{ lineHeight: 1.4, wordBreak: 'break-word' }}>
                          {reviewNote}
                        </Text>
                      </Box>
                    ) : null}
                  </Stack>
                </Popover.Dropdown>
              </Popover>
              {latestRejection ? (
                <Tooltip
                  label={
                    previousRejectionReason
                      ? `Previous submission was rejected: ${previousRejectionReason}`
                      : 'Previous submission was rejected'
                  }
                  multiline
                  maw={320}
                  withArrow
                  position="bottom"
                >
                  <Text
                    size="xs"
                    c="orange.7"
                    truncate
                    style={{
                      lineHeight: 1.2,
                      minWidth: 0,
                      maxWidth: 280,
                      cursor: 'default',
                    }}
                  >
                    {previousRejectionReason
                      ? `Previously rejected · ${previousRejectionReason}`
                      : 'Previously rejected'}
                  </Text>
                </Tooltip>
              ) : null}
            </Group>
          </Group>

          {canAct && uiStatus === 'DRAFT' ? (
            <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
              <Tooltip
                label={
                  hasUnsavedChanges
                    ? 'Save your schedule changes to the server before submitting this draft.'
                    : 'Submit draft for approval'
                }
                multiline={hasUnsavedChanges}
                w={hasUnsavedChanges ? 240 : undefined}
                withArrow
              >
                <span style={{ display: 'inline-flex' }}>
                  <Button
                    {...ganttToolbarButtonProps}
                    color="brand"
                    leftSection={<IconSend size={13} />}
                    loading={busy}
                    disabled={busy || hasUnsavedChanges}
                    onClick={() => void onSubmit()}
                  >
                    Submit draft
                  </Button>
                </span>
              </Tooltip>
              <Tooltip label="Discard this revision" withArrow>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size={28}
                  radius="xs"
                  loading={busy}
                  disabled={busy}
                  aria-label="Discard revision"
                  onClick={() => void onDiscard()}
                >
                  <IconTrash size={14} />
                </ActionIcon>
              </Tooltip>
            </Group>
          ) : null}

          {canAct && uiStatus === 'SUBMITTED' ? (
            <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
              <Button
                {...ganttToolbarButtonProps}
                color="brand"
                leftSection={<IconCheck size={13} />}
                disabled={busy}
                onClick={() => {
                  setInfoOpen(false);
                  setDialog('approve');
                }}
              >
                Approve
              </Button>
              <Button
                {...ganttToolbarButtonProps}
                color="red"
                variant="light"
                leftSection={<IconX size={13} />}
                disabled={busy}
                onClick={() => {
                  setInfoOpen(false);
                  setDialog('reject');
                }}
              >
                Reject
              </Button>
            </Group>
          ) : null}

          {!canAct ? (
            <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
              View only
            </Text>
          ) : null}
        </Group>
      </Box>

      <Modal
        opened={dialog === 'approve'}
        onClose={closeDialog}
        title="Approve baseline revision"
        centered
        size="sm"
        radius="md"
        closeOnClickOutside={!dialogBusy}
        closeOnEscape={!dialogBusy}
        portalProps={modalContainer ? { target: modalContainer } : undefined}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Promote <strong>{name}</strong> to an approved baseline slot.
          </Text>
          <Textarea
            label="Review comment"
            placeholder="Optional note for the submitter"
            minRows={3}
            autosize
            maxRows={5}
            value={reviewComment}
            disabled={dialogBusy}
            onChange={(e) => setReviewComment(e.currentTarget.value)}
          />
          <Checkbox
            label="Set as earned value baseline"
            checked={setAsEv}
            disabled={dialogBusy}
            onChange={(e) => setSetAsEv(e.currentTarget.checked)}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={closeDialog} disabled={dialogBusy}>
              Cancel
            </Button>
            <Button
              loading={dialogBusy}
              disabled={dialogBusy}
              onClick={() =>
                runDialogAction(() =>
                  onApprove({
                    setAsEarnedValueBaseline: setAsEv,
                    reviewComment: reviewComment.trim() || undefined,
                  }),
                )
              }
              {...busyActionButtonProps(dialogBusy, 'brand')}
            >
              Approve
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={dialog === 'reject'}
        onClose={closeDialog}
        title="Reject baseline revision"
        centered
        size="sm"
        radius="md"
        closeOnClickOutside={!dialogBusy}
        closeOnEscape={!dialogBusy}
        portalProps={modalContainer ? { target: modalContainer } : undefined}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Send <strong>{name}</strong> back for changes. A rejection comment is required.
          </Text>
          <Textarea
            label="Review comment"
            placeholder="Explain what needs to change"
            required
            minRows={3}
            autosize
            maxRows={5}
            value={reviewComment}
            disabled={dialogBusy}
            error={
              reviewComment.length > 0 && !canConfirmReject
                ? 'Comment cannot be empty'
                : undefined
            }
            onChange={(e) => setReviewComment(e.currentTarget.value)}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={closeDialog} disabled={dialogBusy}>
              Cancel
            </Button>
            <Button
              loading={dialogBusy}
              disabled={dialogBusy || !canConfirmReject}
              onClick={() =>
                runDialogAction(() => onReject({ reviewComment: rejectComment }))
              }
              {...busyActionButtonProps(dialogBusy, 'red')}
            >
              Reject
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
